import { Pagination, usePaginate, Wrapper } from '@cma/common'
import { DocumentCard } from '@cma/features/document/DocumentCard'
import { CreateReport, ReportsHeader, useReports } from '@cma/features/report'
import { ReportTypeEnum } from '@cma/generated/graphql'
import { Fragment, Suspense } from 'react'
import { Helmet } from 'react-helmet-async'

export default function Documents() {
  const paginate = usePaginate({ limit: 27 })

  return (
    <Wrapper>
      <Helmet>
        <title>Documents - Cloud CMA</title>
      </Helmet>
      <ReportsHeader
        title="Documents"
        placeholder="Search Documents"
        onSubmit={(data) => {
          paginate.setFirst(paginate.limit)
          paginate.setLast(undefined)
          paginate.setAfter(undefined)
          paginate.setBefore(undefined)
          paginate.setSearch(data.search)
        }}
      />
      <Suspense
        fallback={
          <>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <CreateReport to="/documents/new">Create Document</CreateReport>
              {new Array(5).fill('').map((_, index) => (
                <DocumentCard.Skeleton key={index} />
              ))}
            </div>
            <Pagination disablePrev={true} disableNext={true} />
          </>
        }>
        <DocumentCards paginate={paginate} />
      </Suspense>
    </Wrapper>
  )
}

interface DocumentCardsProps {
  paginate: ReturnType<typeof usePaginate>
}

function DocumentCards({ paginate }: DocumentCardsProps) {
  const { data: { reports } = {} } = useReports({
    types: [ReportTypeEnum.Document],
    first: paginate.first,
    last: paginate.last,
    after: paginate.after,
    before: paginate.before,
    search: paginate.search
  })

  return (
    <>
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        <CreateReport to="/documents/new">Create Document</CreateReport>
        {reports?.nodes?.map((report) => (
          <Fragment key={report?.id}>
            {report && <DocumentCard report={report} />}
          </Fragment>
        ))}
      </div>
      <Pagination
        disablePrev={!reports?.pageInfo.hasPreviousPage}
        disableNext={!reports?.pageInfo.hasNextPage}
        onPrevClick={() => {
          paginate.setBefore(reports?.pageInfo.startCursor ?? undefined)
          paginate.setAfter(undefined)
          paginate.setFirst(undefined)
          paginate.setLast(paginate.limit)
        }}
        onNextClick={() => {
          paginate.setBefore(undefined)
          paginate.setAfter(reports?.pageInfo?.endCursor ?? undefined)
          paginate.setFirst(paginate.limit)
          paginate.setLast(undefined)
        }}
      />
    </>
  )
}
